import {
  desktopCSS,
  mobileCSS,
  smallMobileCSS,
  tabletCSS,
} from '@measures/responsive'
import { css, CSSProp, DefaultTheme } from 'styled-components'
import { AdmTagMan, RegisterSlotType } from './types'

const registerSlot: RegisterSlotType = (slotObj) => {
  const admTagMan = window.admTagMan as AdmTagMan
  admTagMan.q.push(() => {
    admTagMan.registerSlot(slotObj)
  })
}

const getMinHeightForMobile = (
  placement: string,
  hasAdBackground?: boolean
) => {
  return hasAdBackground ||
    placement === 'afterArticleTextAd' ||
    placement === 'mobileSectionAd3'
    ? '602.5px'
    : placement === 'mobileRectangle'
    ? '482.5px'
    : '252.5px'
}

const adPlaceholder = ({
  theme,
  minHeight,
  hasAdBackground,
}: {
  theme: DefaultTheme
  minHeight: string
  hasAdBackground: boolean
}) => css`
  border: 1px solid ${theme.colors.grey.grey_30};
  background-color: ${theme.colors.grey.grey_20};
  box-sizing: border-box;
  overflow: hidden;
  height: ${minHeight};
  position: relative;

  ${!hasAdBackground &&
  css`
    &:not(.adm-loaded):before {
      position: absolute;
      max-width: 200px;
      content: var(--adPlaceholderTitle);
      font-weight: normal;
      font-size: 10px;
      line-height: 1;
      color: ${theme.colors.grey.grey_70};
      text-transform: uppercase;
      letter-spacing: 1.5px;
    }
  `}

  &.adm-loaded {
    border: 0;
    background-color: transparent;
    overflow: visible;
    height: auto;
    min-height: ${minHeight};
    position: static;
  }
`

const placeholderCSSForPlacement = (options: {
  theme: DefaultTheme
  placement: string
  slotCode?: string
  slotCodeMobile?: string
  isArticle: boolean
  hasAdBackground: boolean
}): CSSProp => {
  const {
    theme,
    placement,
    slotCode,
    slotCodeMobile,
    isArticle,
    hasAdBackground,
  } = options
  let mobileSlotCodeCSS
  let tabletSlotCodeCSS
  let desktopSlotCodeCSS

  if (slotCode?.startsWith('WBM_')) {
    desktopSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '254px', hasAdBackground })}
      width: ${isArticle ? '100%' : 'calc(100% + 2 * 20px)'};
      margin-left: ${isArticle ? '0' : '-20px'};
      margin-right: ${isArticle ? '0' : '-20px'};
      &.adm-loaded {
        width: auto;
        min-width: ${isArticle ? '100%' : 'calc(100% + 2 * 20px)'};
      }
    `
    tabletSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '254px', hasAdBackground })}
      width: ${isArticle ? '100%' : 'calc(100% + 2 * 54px)'};
      margin-left: ${isArticle ? '0' : '-52px'};
      margin-right: ${isArticle ? '0' : '-52px'};
      &.adm-loaded {
        width: auto;
        min-width: ${isArticle ? '100%' : 'calc(100% + 2 * 54px)'};
      }
    `
  } else if (slotCode?.startsWith('WB_')) {
    desktopSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '254px', hasAdBackground })}
      ${!hasAdBackground &&
      css`
        width: calc(100% + 2 * 20px);
        margin-left: -20px;
        margin-right: -20px;
      `}
      &.adm-loaded {
        width: auto;
        ${!hasAdBackground &&
        css`
          min-width: calc(100% + 2 * 20px);
        `}
      }
    `
    tabletSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '254px', hasAdBackground })}
      ${!hasAdBackground &&
      css`
        width: calc(100% + 2 * 54px);
        margin-left: -52px;
        margin-right: -52px;
      `}
      &.adm-loaded {
        width: auto;
        ${!hasAdBackground &&
        css`
          min-width: calc(100% + 2 * 54px);
        `}
      }
    `
  } else if (slotCode?.startsWith('DS_')) {
    desktopSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '254px', hasAdBackground })}
      ${!hasAdBackground &&
      css`
        width: calc(100% + 2 * 20px);
        margin-left: -20px;
        margin-right: -20px;
      `}
      &.adm-loaded {
        width: auto;
        ${!hasAdBackground &&
        css`
          min-width: calc(100% + 2 * 20px);
        `}
      }
    `
    tabletSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '254px', hasAdBackground })}
      ${!hasAdBackground &&
      css`
        width: calc(100% + 2 * 54px);
        margin-left: -52px;
        margin-right: -52px;
      `}
      &.adm-loaded {
        width: auto;
        ${!hasAdBackground &&
        css`
          min-width: calc(100% + 2 * 54px);
        `}
      }
    `
  } else if (slotCode?.startsWith('TE_')) {
    desktopSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 304px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
    tabletSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 322px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  } else if (slotCode?.startsWith('MR_')) {
    desktopSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 304px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
    tabletSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 322px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  } else if (slotCode?.startsWith('SBA_')) {
    desktopSlotCodeCSS = css`
      display: inline-block;
      position: relative;
      &.adm-empty,
      &.adm-error {
        display: none;
      }
    `
  } else if (slotCode?.startsWith('RECOM_')) {
    desktopSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 100%;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
    tabletSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 100%;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  }

  if (slotCodeMobile?.startsWith('MMR_')) {
    mobileSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '252.5px', hasAdBackground })}
      width: 300px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  } else if (slotCodeMobile?.startsWith('MPA_')) {
    mobileSlotCodeCSS = css`
      ${adPlaceholder({
        theme,
        minHeight: hasAdBackground ? '482.5px' : '252.5px',
        hasAdBackground,
      })}
      width: 300px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  } else if (slotCodeMobile?.startsWith('MDS_')) {
    mobileSlotCodeCSS = css`
      ${adPlaceholder({
        theme,
        minHeight: '252.5px',
        hasAdBackground,
      })}
      width: 300px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  } else if (slotCodeMobile?.startsWith('MHPA_')) {
    mobileSlotCodeCSS = css`
      ${adPlaceholder({
        theme,
        minHeight: getMinHeightForMobile(placement, hasAdBackground),
        hasAdBackground,
      })}
      width: 300px;
      margin: 0 auto;
      &.adm-loaded {
        width: auto;
      }
    `
  }
  //TODO: Check if this should be 172px or can be adjusted
  else if (slotCodeMobile?.startsWith('MTE_')) {
    mobileSlotCodeCSS = css`
      ${adPlaceholder({ theme, minHeight: '160px', hasAdBackground })}
      ${smallMobileCSS(css`
        ${adPlaceholder({ theme, minHeight: '150px', hasAdBackground })}
      `)}
      width: 100%;
    `
  } else if (slotCodeMobile?.startsWith('IAV_')) {
    mobileSlotCodeCSS = css`
      width: 100%;
      margin: 0 auto;
    `
  }

  return css`
    ${desktopCSS(css`
      ${desktopSlotCodeCSS}
    `)}
    ${tabletCSS(css`
      ${tabletSlotCodeCSS}
    `)}
    ${mobileCSS(css`
      ${mobileSlotCodeCSS}
    `)}
  `
}

export { registerSlot, adPlaceholder, placeholderCSSForPlacement }
